import { useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import type { OverdueSale } from "./RecaudoResults";

type Props = {
  overdueSales: OverdueSale[];
  selectedTemplateId: string;
  broadcastName: string;
  onBroadcastNameChange: (name: string) => void;
  whatsappConnectionId: string;
  companyName?: string;
  projectName?: string;
  onSuccess: () => void;
};

const RecaudoConfirmation = ({
  overdueSales,
  selectedTemplateId,
  broadcastName,
  onBroadcastNameChange,
  whatsappConnectionId,
  companyName,
  projectName,
  onSuccess,
}: Props) => {
  const { merchantId, displayName } = useContext(MerchantContext);
  const navigate = useNavigate();

  const { data, isLoading } = trpc.whatsappTemplates.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const selectedTemplate = useMemo(() => {
    if (!data?.templates || !selectedTemplateId) return null;
    return data.templates.find((t) => t.id === selectedTemplateId);
  }, [data?.templates, selectedTemplateId]);

  const buildTemplateVars = useCallback(
    (sale: OverdueSale): Record<string, string> => ({
      // Defaults from template config as base fallback (auth templates have no body)
      ...(selectedTemplate && "body" in selectedTemplate.config
        ? selectedTemplate.config.body.defaults
        : undefined),
      // Shared context vars (Spanish + English keys for template flexibility)
      empresa: companyName ?? "",
      proyecto: projectName ?? "",
      project: projectName ?? "",
      display_name: displayName,
      overdue: String(sale.overdueInstallmentCount),
      // Contact-specific vars override everything
      nombre: sale.buyerFullName,
      name: sale.buyerFullName,
      cuotas_vencidas: String(sale.overdueInstallmentCount),
      installment_count: String(sale.overdueInstallmentCount),
      max_days_overdue: String(sale.maxDaysOverdue),
      outstanding_balance: String(sale.totalOutstandingBalance),
      payment_status: sale.paymentStatus,
      overdue_installments: sale.overdueInstallments
        ? JSON.stringify(sale.overdueInstallments)
        : "[]",
    }),
    [selectedTemplate, companyName, projectName, displayName]
  );

  const previewMessage = useMemo(() => {
    if (!selectedTemplate?.bodyPreview || overdueSales.length === 0) return "";
    const vars = buildTemplateVars(overdueSales[0]);
    return selectedTemplate.bodyPreview.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      return vars[key] ?? `[${key}]`;
    });
  }, [selectedTemplate, overdueSales, buildTemplateVars]);

  const createBroadcastMutation = trpc.broadcast.create.useMutation({
    onSuccess: () => {
      toast.success("Envío masivo creado exitosamente");
      onSuccess();
      navigate("/broadcast");
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al crear el envío masivo");
    },
  });

  const handleSend = () => {
    const recipients = overdueSales.map((v) => ({
      phone: v.buyerMobile ?? v.phone ?? "",
      name: v.buyerFullName,
      templateVariables: buildTemplateVars(v),
    }));

    createBroadcastMutation.mutate({
      merchantId: merchantId || "",
      name: broadcastName.trim(),
      pipelineId: "collections",
      whatsappConnectionId,
      templateId: selectedTemplateId,
      recipients,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="xl" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">Confirmar Envío</p>
        <p className="mb-4 text-sm text-gray-600">
          Revisa la información antes de enviar los mensajes.
        </p>
      </div>

      {/* Broadcast Name Input */}
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">
          Nombre del envío masivo <span className="text-red-500">*</span>
        </Label>
        <Input
          placeholder="Ej: Recaudo Marzo 2026"
          value={broadcastName}
          onChange={(e) => onBroadcastNameChange(e.target.value)}
        />
      </div>

      {/* Summary Card */}
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-purple-600">{overdueSales.length}</p>
              <p className="text-sm text-gray-600">Mensajes a enviar</p>
            </div>
            {selectedTemplate && (
              <div className="flex flex-col items-center gap-1">
                <p className="text-lg font-bold text-purple-600">{selectedTemplate.name}</p>
                <p className="text-sm text-gray-600">Plantilla seleccionada</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      {previewMessage && overdueSales.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col gap-4">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <p className="font-bold">Vista Previa del Mensaje</p>
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                    Ejemplo con primer contacto
                  </Badge>
                </div>
                <p className="mb-3 text-sm text-gray-500">
                  Así se verá el mensaje para: <strong>{overdueSales[0].buyerFullName}</strong>
                </p>
              </div>
              <Separator />
              <Card className="border border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <p className="whitespace-pre-wrap text-sm text-gray-800">{previewMessage}</p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning */}
      <Alert status="warning">
        <p className="text-sm">
          Al hacer clic en "Contactar", se enviarán <strong>{overdueSales.length}</strong> mensajes de
          WhatsApp. Esta acción no se puede deshacer.
        </p>
      </Alert>

      {/* Send Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSend}
          disabled={!broadcastName.trim() || createBroadcastMutation.isLoading}
        >
          {createBroadcastMutation.isLoading ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Enviando...
            </span>
          ) : (
            "Contactar"
          )}
        </Button>
      </div>
    </div>
  );
};

export default RecaudoConfirmation;
